import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import client from '../../../lib/feathers';

const tierLabels = {
  taster: 'Taster',
  collector: 'Collector',
  enthusiast: 'Enthusiast',
  connoisseur: 'Connoisseur',
  vip: 'VIP',
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusBadge = (pass) => {
  if (!pass.isActive) {
    return { label: 'Disabled', classes: 'bg-gray-100 text-gray-800' };
  }
  if (pass.expiresAt && new Date(pass.expiresAt) < new Date()) {
    return { label: 'Expired', classes: 'bg-red-100 text-red-800' };
  }
  if (pass.maxUses && pass.usedCount >= pass.maxUses) {
    return { label: 'Maxed', classes: 'bg-yellow-100 text-yellow-800' };
  }
  return { label: 'Active', classes: 'bg-green-100 text-green-800' };
};

export default function AccessPassDetail() {
  const { apid } = useParams();
  const navigate = useNavigate();
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPass = async () => {
    try {
      setLoading(true);
      setError(null);
      const service = client.service('admin/access-pass');
      const result = await service.get(apid);
      setPass(result);
    } catch (e) {
      console.error('Error loading access pass:', e);
      setError('Failed to load access pass.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPass();
  }, [apid]);

  if (loading) {
    return (
      <Page title="Access Pass">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Page>
    );
  }

  if (error || !pass) {
    return (
      <Page title="Access Pass">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error || 'Access pass not found.'}</p>
        </div>
      </Page>
    );
  }

  const status = getStatusBadge(pass);
  const redemptions = pass.passRedemptions || [];

  return (
    <Page title={`Access Pass: ${pass.code}`}>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <Breadcrumb elements={[
          { name: 'Access Passes', href: '/admin/access-passes' },
          { name: pass.code },
        ]} />
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to={`/admin/access-passes/${pass.id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
          >
            Edit
          </Link>
        </div>
      </div>

      {/* Pass Info */}
      <div className="mt-6 bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 font-mono">{pass.code}</h3>
            {pass.description && (
              <p className="mt-1 max-w-2xl text-sm text-gray-500">{pass.description}</p>
            )}
          </div>
          <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${status.classes}`}>
            {status.label}
          </span>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Tier</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {tierLabels[pass.grantTier] || pass.grantTier}
              </dd>
            </div>
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Duration</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {pass.durationDays} days
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Usage</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {pass.usedCount} / {pass.maxUses || '\u221E'}
              </dd>
            </div>
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Expires</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {pass.expiresAt ? formatDate(pass.expiresAt) : 'Never'}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Active</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {pass.isActive ? 'Yes' : 'No'}
              </dd>
            </div>
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(pass.createdAt)}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Redemptions Table */}
      <div className="mt-8">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
          Redemptions ({redemptions.length})
        </h3>
        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Redeemed At
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Access Expires
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {redemptions.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-6 py-12 text-center text-sm text-gray-500">
                    No redemptions yet.
                  </td>
                </tr>
              ) : (
                redemptions.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {r.user ? (
                        <Link to={`/admin/users/${r.user.id}`} className="text-primary hover:text-red-900">
                          <div className="text-sm font-medium">{r.user.firstName} {r.user.lastName}</div>
                          <div className="text-xs text-gray-500">{r.user.email}</div>
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-500">{r.userId}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(r.redeemedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(r.grantedUntil)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Page>
  );
}
